/**
 * Action Mapper Utility
 * Maps action types to moment data structure paths
 * Ensures consistency across the training module
 */

/**
 * Maps action type to moment data field path
 * @param {string} actionType - Action type ID
 * @param {boolean} isBeforeTouch - Whether this is beforeTouch or afterTouch
 * @returns {Object} Mapping configuration with path and default values
 */
export function getActionMapping(actionType, isBeforeTouch = true) {
  const basePath = isBeforeTouch ? 'beforeTouch' : 'afterTouch';
  
  const mappings = {
    voiceOver: {
      path: `${basePath}.voiceOver`,
      field: 'text',
      defaultValue: '',
      label: 'Voice Over Text',
    },
    textMedia: {
      path: `${basePath}.displayText`,
      field: 'content',
      defaultValue: '',
      label: 'Display Text',
    },
    objects: {
      path: `${basePath}.objects`,
      field: 'query',
      defaultValue: { query: '', option: 'Highlight', spawn: '' },
      label: 'Objects',
      isObject: true,
    },
    soundEffects: {
      path: 'afterTouch.sfx',
      field: 'text',
      defaultValue: '',
      label: 'Sound Effect',
      onlyAfterTouch: true,
    },
    metaLayer: {
      path: `${basePath}.metaLayer`,
      field: 'query',
      defaultValue: '',
      label: 'Meta Layer',
    },
    imageMedia: {
      path: `${basePath}.image`,
      field: 'url',
      defaultValue: '',
      label: 'Image URL',
    },
    videoMedia: {
      path: `${basePath}.video`,
      field: 'url',
      defaultValue: '',
      label: 'Video URL',
    },
    visualEffect: {
      path: 'afterTouch.visualEffect',
      field: 'text',
      defaultValue: '',
      label: 'Visual Effect',
      onlyAfterTouch: true,
    },
    // Advanced action types - stored in custom fields
    holoBooks: {
      path: `${basePath}.holoBooks`,
      field: 'content',
      defaultValue: '',
      label: 'Holo Books Content',
      customField: true,
    },
    highlights: {
      path: `${basePath}.highlights`,
      field: 'query',
      defaultValue: '',
      label: 'Highlights Query',
      customField: true,
    },
    player: {
      path: `${basePath}.player`,
      field: 'action',
      defaultValue: '',
      label: 'Player Action',
      customField: true,
    },
    baseItem: {
      path: `${basePath}.baseItem`,
      field: 'query',
      defaultValue: '',
      label: 'Base Item Query',
      customField: true,
    },
    objAnimation: {
      path: `${basePath}.objAnimation`,
      field: 'animation',
      defaultValue: '',
      label: 'Object Animation',
      customField: true,
    },
    uiUpdate: {
      path: `${basePath}.uiUpdate`,
      field: 'update',
      defaultValue: '',
      label: 'UI Update',
      customField: true,
    },
    timerDelay: {
      path: `${basePath}.timerDelay`,
      field: 'duration',
      defaultValue: 0,
      label: 'Timer Delay (seconds)',
      customField: true,
      isNumber: true,
    },
    animation: {
      path: `${basePath}.animation`,
      field: 'animation',
      defaultValue: '',
      label: 'Animation',
      customField: true,
    },
    stateTable: {
      path: `${basePath}.stateTable`,
      field: 'state',
      defaultValue: '',
      label: 'State Table',
      customField: true,
    },
    checklistUI: {
      path: `${basePath}.checklistUI`,
      field: 'items',
      defaultValue: [],
      label: 'Checklist Items',
      customField: true,
      isArray: true,
    },
  };

  const mapping = mappings[actionType];
  if (!mapping) {
    console.warn(`Unknown action type: ${actionType}`);
    return {
      path: `${basePath}.${actionType}`,
      field: 'value',
      defaultValue: '',
      label: actionType,
      customField: true,
    };
  }

  // Check if action is only allowed in afterTouch
  if (mapping.onlyAfterTouch && isBeforeTouch) {
    return {
      ...mapping,
      path: 'afterTouch.' + mapping.path.split('.').pop(),
    };
  }

  return mapping;
}

/**
 * Get the value of an action from moment data
 * @param {Object} moment - Moment object
 * @param {string} actionType - Action type ID
 * @param {boolean} isBeforeTouch - Whether this is beforeTouch or afterTouch
 * @param {boolean} isTechnical - Whether to use technical or nonTechnical content
 * @returns {*} The value from moment data
 */
export function getActionValue(moment, actionType, isBeforeTouch = true, isTechnical = true) {
  if (!moment || !moment.content) return null;
  
  const content = moment.content[isTechnical ? 'technical' : 'nonTechnical'] || {};
  const mapping = getActionMapping(actionType, isBeforeTouch);
  const keys = mapping.path.split('.');
  
  let value = content;
  for (const key of keys) {
    if (value && typeof value === 'object' && key in value) {
      value = value[key];
    } else {
      return mapping.defaultValue;
    }
  }
  
  if (mapping.isObject && typeof value === 'object') {
    return value;
  }
  
  return value || mapping.defaultValue;
}

/**
 * Set the value of an action in moment data
 * @param {string} momentId - Moment ID
 * @param {Function} onMomentUpdate - Update function
 * @param {string} actionType - Action type ID
 * @param {*} value - Value to set
 * @param {boolean} isBeforeTouch - Whether this is beforeTouch or afterTouch
 * @param {boolean} isTechnical - Whether to use technical or nonTechnical content
 */
export function setActionValue(
  momentId,
  onMomentUpdate,
  actionType,
  value,
  isBeforeTouch = true,
  isTechnical = true
) {
  if (!onMomentUpdate) return;
  
  const mapping = getActionMapping(actionType, isBeforeTouch);
  const modeKey = isTechnical ? 'technical' : 'nonTechnical';
  const fullPath = `content.${modeKey}.${mapping.path}`;
  
  if (mapping.isObject && typeof value === 'object') {
    // Update object fields individually
    if (value.query !== undefined) {
      onMomentUpdate(momentId, `${fullPath}.query`, value.query);
    }
    if (value.option !== undefined) {
      onMomentUpdate(momentId, `${fullPath}.option`, value.option);
    }
    if (value.spawn !== undefined) {
      onMomentUpdate(momentId, `${fullPath}.spawn`, value.spawn);
    }
  } else {
    onMomentUpdate(momentId, fullPath, value);
  }
}

/**
 * Check if an action type is supported in beforeTouch
 * @param {string} actionType - Action type ID
 * @returns {boolean}
 */
export function isBeforeTouchAction(actionType) {
  const onlyAfterTouch = ['soundEffects', 'visualEffect'];
  return !onlyAfterTouch.includes(actionType);
}

/**
 * Check if an action type is supported in afterTouch
 * @param {string} actionType - Action type ID
 * @returns {boolean}
 */
export function isAfterTouchAction(actionType) {
  return true; // All actions can be in afterTouch
}

